import { ColumnMetadata } from "~/components/DataGrid/types";
import { AssetColumns } from "~/modules/assets/types";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { ASSET_NAME_AND_TICKER_COLUMN_NON_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import {
    MAP_METRIC_SLUG_TO_ASSET_TABLE_COLUMN,
    MAP_REFERENCE_DATA_SLUG_TO_ASSET_TABLE_COLUMN,
} from "~/modules/catalog/components/CatalogAssetsTable/getAssetTableColumnByCatalogSlug";
import {
    CatalogItem,
    CatalogMetricSlugs,
    CatalogReferenceDataSlugs,
} from "~/modules/catalog/types";
import { AssetTableColumnConfig } from "~/modules/charts/types";

// // // //

/** Every slug the catalog can hand us, metric or reference data. */
type CatalogSlug = CatalogMetricSlugs | CatalogReferenceDataSlugs;

/**
 * MAP_CATALOG_SLUG_TO_ASSET_COLUMN
 * The one place catalog slugs and `AssetColumns` values are tied together.
 * Every slug in both catalog enums has an entry, so adding a slug to either
 * enum fails to compile until it is mapped here.
 *
 * `null` means the catalog field has no `AssetColumns` counterpart at all - it
 * is metadata the asset table has no column for. A mapped value only says the
 * two describe the same field; whether an `asset_table` chart can render it is
 * a separate question, answered by `isAssetTableColumnSlug`.
 */
export const MAP_CATALOG_SLUG_TO_ASSET_COLUMN: Record<
    CatalogMetricSlugs | CatalogReferenceDataSlugs,
    AssetColumns | null
> = {
    // ---- Metrics
    [CatalogMetricSlugs.adjusted_daily_burns_dollar]:
        AssetColumns.adjustedDailyBurnsDollar,
    [CatalogMetricSlugs.adjusted_daily_burns_token]:
        AssetColumns.adjustedDailyBurnsToken,
    [CatalogMetricSlugs.adjusted_daily_mints_dollar]:
        AssetColumns.adjustedDailyMintsDollar,
    [CatalogMetricSlugs.adjusted_daily_mints_token]:
        AssetColumns.adjustedDailyMintsToken,
    [CatalogMetricSlugs.annually_active_addresses_count]:
        AssetColumns.annuallyActiveAddressesCount,
    [CatalogMetricSlugs.apy_30_day]: AssetColumns.apy30Day,
    [CatalogMetricSlugs.apy_7_day]: AssetColumns.apy7Day,
    [CatalogMetricSlugs.bridged_token_market_cap_dollar]:
        AssetColumns.bridgedTokenMarketCapDollar,
    [CatalogMetricSlugs.bridged_token_supply_token]:
        AssetColumns.bridgedTokenSupplyToken,
    [CatalogMetricSlugs.bridged_token_value_dollar]:
        AssetColumns.bridgedTokenValueDollar,
    [CatalogMetricSlugs.circulating_asset_value_dollar]:
        AssetColumns.circulatingAssetValueDollar,
    [CatalogMetricSlugs.circulating_market_value_dollar]:
        AssetColumns.circulatingMarketValueDollar,
    [CatalogMetricSlugs.circulating_supply_token]:
        AssetColumns.circulatingSupplyToken,
    [CatalogMetricSlugs.cumulative_issued_amount_dollar]:
        AssetColumns.cumulativeIssuedAmountDollar,
    [CatalogMetricSlugs.cumulative_issued_amount_token]:
        AssetColumns.cumulativeIssuedAmountToken,
    [CatalogMetricSlugs.daily_active_addresses_count]:
        AssetColumns.dailyActiveAddressesCount,
    [CatalogMetricSlugs.daily_burns_dollar]: AssetColumns.dailyBurnsDollar,
    [CatalogMetricSlugs.daily_burns_token]: AssetColumns.dailyBurnsToken,
    [CatalogMetricSlugs.daily_mints_dollar]: AssetColumns.dailyMintsDollar,
    [CatalogMetricSlugs.daily_mints_token]: AssetColumns.dailyMintsToken,
    [CatalogMetricSlugs.daily_transfer_volume_dollar]:
        AssetColumns.dailyTransferVolumeDollar,
    [CatalogMetricSlugs.daily_transfer_volume_token]:
        AssetColumns.dailyTransferVolumeToken,
    [CatalogMetricSlugs.daily_yield_distributed_dollar]:
        AssetColumns.dailyYieldDistributedDollar,
    [CatalogMetricSlugs.daily_yield_rate]: AssetColumns.dailyYieldRate,
    [CatalogMetricSlugs.holding_addresses_count]:
        AssetColumns.holdingAddressesCount,
    [CatalogMetricSlugs.hypothetical_10_000_performance]:
        AssetColumns.hypothetical_10_000_performance,
    [CatalogMetricSlugs.market_value_dollar]: AssetColumns.marketValueDollar,
    [CatalogMetricSlugs.monthly_active_addresses_count]:
        AssetColumns.monthlyActiveAddressesCount,
    [CatalogMetricSlugs.monthly_transfer_count]:
        AssetColumns.monthlyTransferCount,
    [CatalogMetricSlugs.monthly_transfer_volume]:
        AssetColumns.monthlyTransferVolume,
    [CatalogMetricSlugs.net_asset_value_dollar]:
        AssetColumns.netAssetValueDollar,
    [CatalogMetricSlugs.number_of_daily_transactions_count]:
        AssetColumns.numberOfDailyTransactionsCount,
    [CatalogMetricSlugs.price_dollar]: AssetColumns.priceDollar,
    [CatalogMetricSlugs.quarterly_active_addresses_count]:
        AssetColumns.quarterlyActiveAddressesCount,
    [CatalogMetricSlugs.top_10_holder_concentration]:
        AssetColumns.top10HolderConcentration,
    [CatalogMetricSlugs.top_5_holder_concentration]:
        AssetColumns.top5HolderConcentration,
    [CatalogMetricSlugs.top_holder_concentration]:
        AssetColumns.topHolderConcentration,
    [CatalogMetricSlugs.total_asset_value_dollar]:
        AssetColumns.totalAssetValueDollar,
    [CatalogMetricSlugs.total_supply_token]: AssetColumns.totalSupplyToken,
    [CatalogMetricSlugs.trailing_1_month_return_percent]:
        AssetColumns.trailing1MonthReturnPercent,
    [CatalogMetricSlugs.trailing_1_year_return_percent]:
        AssetColumns.trailing1YearReturnPercent,
    [CatalogMetricSlugs.trailing_30_day_active_addresses_count]:
        AssetColumns.trailing30DayActiveAddressesCount,
    [CatalogMetricSlugs.trailing_30_day_transfer_count]:
        AssetColumns.trailing30DayTransferCount,
    [CatalogMetricSlugs.trailing_30_day_transfer_volume]:
        AssetColumns.trailing30DayTransferVolume,
    [CatalogMetricSlugs.trailing_7_day_active_addresses_count]:
        AssetColumns.trailing7DayActiveAddressesCount,
    [CatalogMetricSlugs.trailing_7_day_transfer_count]:
        AssetColumns.trailing7DayTransferCount,
    [CatalogMetricSlugs.trailing_7_day_transfer_volume]:
        AssetColumns.trailing7DayTransferVolume,
    [CatalogMetricSlugs.weekly_active_addresses_count]:
        AssetColumns.weeklyActiveAddressesCount,
    [CatalogMetricSlugs.weekly_transfer_count]:
        AssetColumns.weeklyTransferCount,
    [CatalogMetricSlugs.weekly_transfer_volume]:
        AssetColumns.weeklyTransferVolume,

    // ---- Reference data
    [CatalogReferenceDataSlugs.address]: null,
    [CatalogReferenceDataSlugs.type]: AssetColumns.type,
    [CatalogReferenceDataSlugs.annual_return_target_description]:
        AssetColumns.annualReturnTargetDescription,
    [CatalogReferenceDataSlugs.asset_class]: AssetColumns.assetClassName,
    [CatalogReferenceDataSlugs.asset_to_pegged_asset_ratio]:
        AssetColumns.assetToPeggedAssetRatio,
    [CatalogReferenceDataSlugs.auditor]: AssetColumns.auditorID,
    [CatalogReferenceDataSlugs.primary_market__base_asset_ticker]:
        AssetColumns.primaryMarketBaseAssetTicker,
    [CatalogReferenceDataSlugs.crypto_broker]: AssetColumns.cryptoBrokerID,
    [CatalogReferenceDataSlugs.crypto_custodian]:
        AssetColumns.cryptoCustodianID,
    [CatalogReferenceDataSlugs.cusip]: AssetColumns.cusip,
    [CatalogReferenceDataSlugs.decimals]: null,
    [CatalogReferenceDataSlugs.description]: AssetColumns.description,
    [CatalogReferenceDataSlugs.dispute_resolution_country]:
        AssetColumns.disputeResolutionCountry,
    [CatalogReferenceDataSlugs.dispute_resolution_description]:
        AssetColumns.disputeResolutionDescription,
    [CatalogReferenceDataSlugs.distributes_income]:
        AssetColumns.distributesIncome,
    [CatalogReferenceDataSlugs.distribution_frequency_type]:
        AssetColumns.distributionFrequencyType,
    [CatalogReferenceDataSlugs.domicile]:
        AssetColumns.legalStructureCountryName,
    [CatalogReferenceDataSlugs.primary_market__investor_types]:
        AssetColumns.primaryMarketInvestorTypes,
    [CatalogReferenceDataSlugs.exchange_agent]: AssetColumns.exchangeAgentID,
    [CatalogReferenceDataSlugs.fee_structure_description]:
        AssetColumns.feeStructureDescription,
    [CatalogReferenceDataSlugs.fund_administrator]:
        AssetColumns.fundAdministratorID,
    [CatalogReferenceDataSlugs.governing_body]: AssetColumns.governingBody,
    [CatalogReferenceDataSlugs.holder_has_right_to_redeem]:
        AssetColumns.holderHasRightToRedeem,
    [CatalogReferenceDataSlugs.holder_rights_description]:
        AssetColumns.holderRightsDescription,
    [CatalogReferenceDataSlugs.icon_url]: AssetColumns.iconUrl,
    [CatalogReferenceDataSlugs.id]: AssetColumns.id,
    [CatalogReferenceDataSlugs.inception_date]: AssetColumns.inceptionDate,
    [CatalogReferenceDataSlugs.isin]: AssetColumns.isin,
    [CatalogReferenceDataSlugs.primary_market__is_kyc_required]:
        AssetColumns.primaryMarketIsKYCRequired,
    [CatalogReferenceDataSlugs.is_open_ended]: AssetColumns.isOpenEnded,
    [CatalogReferenceDataSlugs.issuer]: AssetColumns.issuerName,
    [CatalogReferenceDataSlugs.jurisdiction_country]:
        AssetColumns.jurisdictionCountryName,
    [CatalogReferenceDataSlugs.primary_market__kyc_provider_id]:
        AssetColumns.primaryMarketKYCProviderID,
    [CatalogReferenceDataSlugs.lockup_description]:
        AssetColumns.lockupDescription,
    [CatalogReferenceDataSlugs.lockup_period_months]:
        AssetColumns.lockupPeriodMonths,
    [CatalogReferenceDataSlugs.total_management_fee_bps]:
        AssetColumns.totalManagementFeeBps,
    [CatalogReferenceDataSlugs.manager]: AssetColumns.managerID,
    [CatalogReferenceDataSlugs.primary_market__minimum_subscription_amt]:
        AssetColumns.primaryMarketMinimumSubscriptionAmount,
    [CatalogReferenceDataSlugs.name]: AssetColumns.name,
    [CatalogReferenceDataSlugs.network]: AssetColumns.networkIDs,
    [CatalogReferenceDataSlugs.other_fees_description]:
        AssetColumns.otherFeesDescription,
    [CatalogReferenceDataSlugs.paying_agent]: AssetColumns.payingAgentID,
    [CatalogReferenceDataSlugs.pegged_asset]: AssetColumns.peggedAssetName,
    [CatalogReferenceDataSlugs.peg_maintenance_description]:
        AssetColumns.pegMaintenanceDescription,
    [CatalogReferenceDataSlugs.total_performance_fee_bps]:
        AssetColumns.totalPerformanceFeeBps,
    [CatalogReferenceDataSlugs.platform]: AssetColumns.platformIDs,
    [CatalogReferenceDataSlugs.primary_market]: AssetColumns.primaryMarketID,
    [CatalogReferenceDataSlugs.primary_market__redemption_description]:
        AssetColumns.primaryMarketRedemptionDescription,
    [CatalogReferenceDataSlugs.primary_market__redemption_fee_bps]:
        AssetColumns.primaryMarketRedemptionFeeBps,
    [CatalogReferenceDataSlugs.primary_market__redemption_frequency]:
        AssetColumns.primaryMarketRedemptionFrequency,
    [CatalogReferenceDataSlugs.region_ids]: AssetColumns.regionIDs,
    [CatalogReferenceDataSlugs.regulatory_framework]:
        AssetColumns.regulatoryFramework,
    [CatalogReferenceDataSlugs.sector_ids]: AssetColumns.sectorIDs,
    [CatalogReferenceDataSlugs.standards]: null,
    [CatalogReferenceDataSlugs.sub_advisor]: AssetColumns.subAdvisorID,
    [CatalogReferenceDataSlugs.primary_market__subscription_description]:
        AssetColumns.primaryMarketSubscriptionDescription,
    [CatalogReferenceDataSlugs.primary_market__subscription_fee_bps]:
        AssetColumns.primaryMarketSubscriptionFeeBps,
    [CatalogReferenceDataSlugs.primary_market__subscription_frequency]:
        AssetColumns.primaryMarketSubscriptionFrequency,
    [CatalogReferenceDataSlugs.term_description]: AssetColumns.termDescription,
    [CatalogReferenceDataSlugs.term_length_years]: AssetColumns.termLengthYears,
    [CatalogReferenceDataSlugs.ticker]: AssetColumns.ticker,
    [CatalogReferenceDataSlugs.tokenization_type]:
        AssetColumns.tokenizationTypeIDs,
    [CatalogReferenceDataSlugs.traditional_broker]:
        AssetColumns.traditionalBrokerID,
    [CatalogReferenceDataSlugs.traditional_custodian]:
        AssetColumns.traditionalCustodianID,
    [CatalogReferenceDataSlugs.transferability_description]: null,
    [CatalogReferenceDataSlugs.transferability_type]:
        AssetColumns.transferabilityTypeIDs,
    [CatalogReferenceDataSlugs.tokenization_sponsorship_type]:
        AssetColumns.tokenizationSponsorshipType,
    [CatalogReferenceDataSlugs.transfer_agent]: AssetColumns.transferAgentID,
    [CatalogReferenceDataSlugs.website]: AssetColumns.website,
};

/**
 * MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN
 * The `AssetTable` definition a chart renders for each column, derived from the
 * catalog's own column maps by way of `MAP_CATALOG_SLUG_TO_ASSET_COLUMN`. A
 * column the catalog gains becomes renderable without a second registry to keep
 * in sync; `CHART_ONLY_ASSET_TABLE_COLUMNS` covers what the catalog can't
 * supply, and wins where both have an entry.
 *
 * Keyed by `AssetColumns` because that is what an `asset_table` chart persists
 * in `config.table_columns`, which keeps saved configs stable even if the
 * underlying column constants are renamed or moved.
 */
export const MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN: Partial<
    Record<AssetColumns, ColumnMetadata<AssetTableRowData, AssetColumns>>
> = Object.entries(MAP_CATALOG_SLUG_TO_ASSET_COLUMN).reduce<
    Partial<
        Record<AssetColumns, ColumnMetadata<AssetTableRowData, AssetColumns>>
    >
>((accumulated, [slug, column]) => {
    if (!column || accumulated[column]) return accumulated;

    // A catalog field can map to several columns, or to none at all - the
    // first is the one the table renders for it
    const [definition] =
        MAP_METRIC_SLUG_TO_ASSET_TABLE_COLUMN[slug as CatalogMetricSlugs] ??
        MAP_REFERENCE_DATA_SLUG_TO_ASSET_TABLE_COLUMN[
            slug as CatalogReferenceDataSlugs
        ] ??
        [];

    if (definition) {
        accumulated[column] = definition;
    }

    return accumulated;
}, {});

/**
 * getAssetColumnForCatalogSlug
 * The table column a catalog item describes, or null when the catalog field
 * has no column counterpart.
 */
export function getAssetColumnForCatalogSlug(
    slug: string,
): AssetColumns | null {
    return MAP_CATALOG_SLUG_TO_ASSET_COLUMN[slug as CatalogSlug] ?? null;
}

/**
 * getAssetColumnLabels
 * Every table column the catalog names, keyed by the `AssetColumns` value a
 * chart persists. This is what the selected-column chips read from, so a chip
 * and the picker row that added it always carry the same words.
 *
 * The first catalog item to claim a column wins, matching how the picker
 * dedupes its rows. Columns the catalog doesn't name keep the label their own
 * definition carries - which is also what every chip reads while the catalog
 * is still loading.
 */
export function getAssetColumnLabels(
    items: CatalogItem[],
): Partial<Record<AssetColumns, string>> {
    const labels: Partial<Record<AssetColumns, string>> = {};

    for (const item of items) {
        const column = getAssetColumnForCatalogSlug(item.slug);
        if (column && !labels[column]) {
            labels[column] = item.name;
        }
    }

    for (const [column, definition] of Object.entries(
        MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN,
    )) {
        const assetColumn = column as AssetColumns;
        if (!labels[assetColumn] && typeof definition?.label === "string") {
            labels[assetColumn] = definition.label;
        }
    }

    return labels;
}

/**
 * isAssetTableColumnSlug
 * Narrows an arbitrary value to a selectable column slug, so an unknown entry
 * in a stored config is dropped instead of rendering an empty column.
 */
export function isAssetTableColumnSlug(value: unknown): value is AssetColumns {
    return (
        typeof value === "string" &&
        value in MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN
    );
}

/**
 * getAssetTableColumns
 * Resolves an `asset_table` chart's configured columns into the column
 * definitions the `AssetTable` renders, always led by the asset name column.
 *
 * Each resolved column's `slug` is set to the configured `AssetColumns` value:
 * the underlying definitions don't all carry distinct slugs (several share one),
 * and the data grid keys its cells on `slug`.
 */
export function getAssetTableColumns(
    tableColumns: AssetTableColumnConfig[],
): ColumnMetadata<AssetTableRowData, AssetColumns>[] {
    const seen = new Set<AssetColumns>();

    const selectedColumns = tableColumns.reduce<
        ColumnMetadata<AssetTableRowData, AssetColumns>[]
    >((accumulated, tableColumn) => {
        const { slug } = tableColumn;
        const column = MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN[slug];

        if (!column || seen.has(slug)) {
            return accumulated;
        }

        seen.add(slug);
        accumulated.push({ ...column, slug });

        return accumulated;
    }, []);

    return [ASSET_NAME_AND_TICKER_COLUMN_NON_EXPANDABLE, ...selectedColumns];
}
